/**
 * Public terms of service page. Linked from the toll-free SMS verification
 * form and from the platform footer. Plain prose; not legal advice.
 */
import React from 'react';
import { Link } from 'react-router-dom';

const TermsOfServicePage: React.FC = () => {
  return (
    <div className="min-h-screen bg-apple-bg font-sans antialiased">
      <header className="fixed inset-x-0 top-0 z-40 bg-apple-bg/80 backdrop-blur supports-[backdrop-filter]:bg-apple-bg/70">
        <div className="mx-auto flex h-12 max-w-6xl items-center justify-between px-6">
          <Link to="/" className="text-sm font-semibold tracking-tight text-apple-ink">
            VentureOS
          </Link>
          <Link to="/" className="text-sm text-apple-subtext hover:text-apple-ink">
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-6 pt-24 pb-16 text-apple-ink">
        <h1 className="text-4xl font-semibold tracking-tight mb-2">Terms of Service</h1>
        <p className="text-sm text-apple-subtext mb-10">
          Effective: April 26, 2026 · Operated by VentureOS LLC
        </p>

        <section className="prose prose-sm max-w-none space-y-6 leading-relaxed text-[15px]">
          <h2 className="text-xl font-semibold mt-8">1. Acceptance</h2>
          <p>
            By accessing or using VentureOS ("the Service"), operated
            by <strong>VentureOS LLC</strong> ("we", "us"), you agree to
            these Terms of Service. If you do not agree, do not use the
            Service.
          </p>

          <h2 className="text-xl font-semibold mt-8">2. The Service</h2>
          <p>
            VentureOS is a private venture-capital deal-sourcing,
            intelligence, and pipeline-management tool. Access is by invitation
            and authorized account only. We may modify, suspend, or discontinue
            features at any time.
          </p>

          <h2 className="text-xl font-semibold mt-8">3. Account &amp; eligibility</h2>
          <p>
            You must be at least 18 years old and authorized by your
            organization to use the Service on its behalf. You are responsible
            for keeping your login credentials confidential and for all
            activity under your account.
          </p>

          <h2 className="text-xl font-semibold mt-8">4. Acceptable use</h2>
          <p>You agree not to:</p>
          <ul className="list-disc list-inside space-y-2">
            <li>Use the Service to violate any law, regulation, or third-party right.</li>
            <li>Reverse-engineer, scrape, or attempt to extract source code beyond what is publicly published.</li>
            <li>Resell, sublicense, or expose the Service to unauthorized parties.</li>
            <li>Send unsolicited commercial messages through the Service's SMS or email infrastructure.</li>
            <li>Submit data you do not have the right to share, including personal data of individuals who have not consented.</li>
          </ul>

          <h2 className="text-xl font-semibold mt-8">5. SMS messaging</h2>
          <p>
            The toll-free number <strong>+1-833-841-8666</strong> is restricted
            to internal admin use by pre-authorized staff. Reply{' '}
            <strong>STOP</strong> at any time to opt out;{' '}
            <strong>HELP</strong> for help. Standard message and data rates
            from your carrier may apply. By texting the number, you confirm you
            are an authorized internal user and have consented to receive a
            confirmation reply.
          </p>

          <h2 className="text-xl font-semibold mt-8">6. Intellectual property</h2>
          <p>
            The Service, including its software, algorithms, scoring rubrics,
            and aggregated insights, is owned by VentureOS LLC and protected
            by copyright and other laws. You retain ownership of data you
            submit; you grant us a non-exclusive license to process that data
            solely to operate the Service for you.
          </p>

          <h2 className="text-xl font-semibold mt-8">7. AI-generated outputs</h2>
          <p>
            The Service uses third-party AI providers (including Anthropic
            Claude, OpenAI, Deepgram) to generate dossiers, summaries, match
            scores, sentiment analyses, and chat responses. AI outputs may
            contain inaccuracies. <strong>Do not rely on AI outputs as
            investment advice, legal advice, or as the sole basis for any
            material decision.</strong> Verify important facts independently.
          </p>

          <h2 className="text-xl font-semibold mt-8">8. Disclaimer of warranties</h2>
          <p>
            The Service is provided <strong>"AS IS"</strong> and <strong>"AS
            AVAILABLE"</strong>, without warranties of any kind, express or
            implied, including merchantability, fitness for a particular
            purpose, non-infringement, accuracy, or uninterrupted operation.
          </p>

          <h2 className="text-xl font-semibold mt-8">9. Limitation of liability</h2>
          <p>
            To the maximum extent permitted by law, VentureOS LLC and its
            officers, employees, and contractors shall not be liable for any
            indirect, incidental, special, consequential, or punitive damages,
            or any loss of profits, revenue, or data, arising from your use of
            or inability to use the Service. Our total aggregate liability for
            any claim arising from the Service shall not exceed the greater of
            US$100 or the fees you paid us in the 12 months preceding the
            claim.
          </p>

          <h2 className="text-xl font-semibold mt-8">10. Indemnification</h2>
          <p>
            You agree to indemnify and hold VentureOS LLC harmless from any
            claim arising from your use of the Service, your violation of
            these Terms, or your violation of any third-party right.
          </p>

          <h2 className="text-xl font-semibold mt-8">11. Termination</h2>
          <p>
            We may suspend or terminate your access at any time, with or
            without cause. You may stop using the Service at any time. Sections
            6, 8, 9, 10, and 12 survive termination.
          </p>

          <h2 className="text-xl font-semibold mt-8">12. Governing law</h2>
          <p>
            These Terms are governed by the laws of the State of Texas, without
            regard to conflict-of-laws rules. Any dispute shall be resolved in
            the state or federal courts located in Travis County, Texas.
          </p>

          <h2 className="text-xl font-semibold mt-8">13. Changes</h2>
          <p>
            We may revise these Terms. Material changes will be posted on this
            page with a new effective date. Continued use after the change
            constitutes acceptance.
          </p>

          <h2 className="text-xl font-semibold mt-8">14. Contact</h2>
          <p>
            Questions? Email{' '}
            <a href="mailto:hello@getventureos.ai" className="text-apple-blue underline">hello@getventureos.ai</a>.
          </p>
        </section>
      </main>

      <footer className="border-t border-apple-divider bg-apple-bg px-6 py-8 text-center text-xs text-apple-subtext">
        &copy; {new Date().getFullYear()} VentureOS LLC{' '}
        <Link to="/privacy" className="hover:text-apple-ink ml-2">Privacy</Link>
      </footer>
    </div>
  );
};

export default TermsOfServicePage;
